import React, { useEffect, useState } from 'react';
import './UserList.css';
import { useToast } from "../components/Toast";
import useCurrentUser from "../hooks/useCurrentUser";

const backend_url = process.env.REACT_APP_BACKEND_URL;
const FIXED_ADMIN_EMAIL = "admin@lightbeam.ai";
const ROLES = ["admin", "platform", "cse", "se_sa", "dev", "user"];

// Shown as hover tooltips (mirrors core/rbac.py ROLE_DESCRIPTIONS).
const ROLE_DESCRIPTIONS = {
  admin: "Full access to all clusters and user management.",
  platform: "Create/view dev clusters; view customer clusters.",
  cse: "Create/view customer clusters, manage edge credentials, delete clusters.",
  se_sa: "Create/view customer clusters, manage edge credentials, mark clusters for deletion.",
  dev: "View dev clusters only.",
  user: "No privileges (default).",
};

const UserList = () => {
  const [users, setUsers] = useState([]);
  const { showToast } = useToast();
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.role === "admin";

  const authHeaders = () => {
    const token = localStorage.getItem('authToken') || 'dev-token';
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  };

  const fetchUsers = async () => {
    const res = await fetch(`${backend_url}/api/users`, { headers: authHeaders() });
    const data = await res.json();
    setUsers(data.users || []);
  };

  useEffect(() => { fetchUsers(); }, []);

  const assignRole = async (email, role) => {
    const res = await fetch(`${backend_url}/api/users/assign-role`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ email, role }),
    });
    const data = await res.json();
    if (!data.ok) {
      showToast(data.detail || data.message || "Failed to update role", "error");
      return;
    }
    showToast(data.message || "Role updated", "success");
    fetchUsers();
  };

  return (
    <div className="cluster-container">
      <h2>User Management</h2>
      {!isAdmin && (
        <p className="rbac-note">
          You can view users and roles. Only an admin can change roles.
        </p>
      )}

      <table className="cluster-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>Joined</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {users.map((u) => {
            const isFixedAdmin = u.email === FIXED_ADMIN_EMAIL;
            return (
              <tr key={u.id}>
                <td>{u.email}</td>
                <td>{u.name}</td>
                <td title={ROLE_DESCRIPTIONS[u.role] || ""}>{u.role}</td>
                <td>{u.created_at ? new Date(u.created_at).toLocaleString() : "—"}</td>
                <td>
                  {/* Only admins get the role dropdown; the seed admin is locked. */}
                  {!isAdmin ? (
                    <span className="muted">—</span>
                  ) : isFixedAdmin ? (
                    <button className="btn disabled-btn" disabled>Admin (locked)</button>
                  ) : (
                    <select
                      className="role-select"
                      value={u.role}
                      title={ROLE_DESCRIPTIONS[u.role] || ""}
                      onChange={(e) => assignRole(u.email, e.target.value)}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r} title={ROLE_DESCRIPTIONS[r]}>{r}</option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default UserList;
